import { useCallback, useRef } from 'react'

import { FinancialWorkspaceValueObjectsPeriodEnum } from 'generated/resources/graphql'
import isEqual from 'lodash/isEqual'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { getPeriodRange } from 'modules/finance/components/FinancialWorkspace/lib/trackDateProperties'

interface TrackReportRunPayload {
  period: FinancialWorkspaceValueObjectsPeriodEnum
  page: string
  group?: string
  formattedStartDate: string
  formattedEndDate: string
  costCenterIds?: string[]
  locationIds?: string[]
  conceptIds?: string[]
  legalEntityIds?: string[]
  employeeIds?: string[]
  search: string
  customPayload?: Record<string, boolean>
}

export const useTrackReportRun = () => {
  const lastIdempotencyKey = useRef({})

  return useCallback((onPageLoad: boolean, payload: TrackReportRunPayload) => {
    const trackingPayload = {
      page: payload.page,
      ...(payload.customPayload ?? {}),
      'filter:by_legal_entities': !!payload.legalEntityIds?.length,
      'filter:by_cost_centers': !!payload.costCenterIds?.length,
      'filter:by_workplaces': !!payload.locationIds?.length,
      'filter:by_payroll_lines': !!payload.conceptIds?.length,
      'filter:by_search': !!payload.search.length,
      'filter:by_employees': !!payload.employeeIds?.length,
      start_date: payload.formattedStartDate,
      end_date: payload.formattedEndDate,
      group_by: payload.group,
      date_range: getPeriodRange(payload.formattedStartDate, payload.formattedEndDate),
    }

    // Only track if idempotency key is different from last time
    if (!isEqual(lastIdempotencyKey.current, trackingPayload)) {
      if (onPageLoad) {
        track(trackingEvents.accounting.financialWorkspace.reportRun, {
          ...trackingPayload,
          is_onpage_load_run: true,
        })
      } else {
        track(trackingEvents.accounting.financialWorkspace.userInitiatedReportRun, {
          ...trackingPayload,
          is_onpage_load_run: false,
        })
      }
      lastIdempotencyKey.current = trackingPayload
    }
  }, [])
}
